import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div>
      <h1>Puzzlify – Privacy Policy</h1>
      <p><em>Last updated: 8 June 2025</em></p>

      <p>Your privacy matters to us. This Policy explains how <strong>Mountainsoft LLC</strong>, doing business as <strong>Puzzlify</strong> (“<strong>Mountainsoft</strong>,” “<strong>Puzzlify</strong>,” “<strong>we</strong>,” “<strong>our</strong>”), collects, uses, and protects information when you visit <code>puzzlify.io</code> or any related services (the “<strong>Service</strong>”).</p>

      <h2>1&nbsp;–&nbsp;Who We Are &amp; How to Contact Us</h2>
      <p>Puzzlify is an online chess‑training platform owned and operated by <strong>Mountainsoft LLC</strong>, a United States limited liability company. Mountainsoft LLC is the data controller responsible for safeguarding your information.</p>
      <p><strong>Email for all privacy inquiries:</strong> <a href="mailto:puzzlify@puzzlify.io">puzzlify@puzzlify.io</a></p>
      <p><small>(We do not maintain a postal address at this time; e‑mail is our primary contact channel for Mountainsoft LLC.)</small></p>

      <h2>2&nbsp;–&nbsp;Information We Collect</h2>

      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Details</th>
            <th>Legal basis&nbsp;(GDPR)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Account Data</strong></td>
            <td>E‑mail address, lichess username</td>
            <td>Contract (to provide the Service)</td>
          </tr>
          <tr>
            <td><strong>Training Stats</strong></td>
            <td>Puzzle IDs completed, accuracy, time‑per‑puzzle, subscription tier</td>
            <td>Contract / Legitimate interest</td>
          </tr>
          <tr>
            <td><strong>Payment Data</strong></td>
            <td>Handled by <strong>Stripe</strong>; we receive only tokenized IDs and billing status—never full card data</td>
            <td>Contract</td>
          </tr>
          <tr>
            <td><strong>Technical Data</strong></td>
            <td>IP address, browser type, device details, session cookies (for login)</td>
            <td>Legitimate interest (security &amp; fraud prevention)</td>
          </tr>
        </tbody>
      </table>

      <p>We currently <strong>do not</strong> run third‑party analytics, tracking pixels, or advertisements. If that changes, we will update this Policy and, where required, request consent.</p>

      <h2>3&nbsp;–&nbsp;How We Use Information</h2>
      <ul>
        <li>Create and manage your account</li>
        <li>Deliver puzzles and track your training progress</li>
        <li>Process payments, renewals, and send subscription reminders (sent via Stripe)</li>
        <li>Enforce our <a href="/terms-of-service">Terms of Service</a>, detect fraud, and maintain security</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>4&nbsp;–&nbsp;Cookies &amp; Similar Technology</h2>
      <p>We set a single <strong>session cookie</strong> to keep you logged in. It expires when you sign out or after 30 days of inactivity. No third‑party or advertising cookies are set at this time.</p>

      <h2>5&nbsp;–&nbsp;Sharing &amp; Disclosure</h2>
      <p>We do <em>not</em> sell your personal information. Mountainsoft LLC shares only what is necessary with the following partners so we can operate the Service:</p>

      <table>
        <thead>
          <tr>
            <th>Recipient</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Stripe, Inc.</strong></td>
            <td>Payment processing and subscription management</td>
          </tr>
          <tr>
            <td><strong>Amazon Web Services (AWS)</strong></td>
            <td>Cloud hosting in a U.S. data center</td>
          </tr>
          <tr>
            <td>Law‑enforcement authorities</td>
            <td>Only if legally compelled (e.g., court order or subpoena)</td>
          </tr>
        </tbody>
      </table>

      <p>If we introduce advertising or analytics partners in the future, we will list them here and seek consent where required.</p>

      <h2>6&nbsp;–&nbsp;International Data Transfers</h2>
      <p>Our servers are located in the <strong>United States</strong>. By using the Service, you understand that your information will be transferred to and processed in the U.S. We rely on Standard Contractual Clauses, the UK Addendum, or equivalent safeguards when required for EU/UK users.</p>

      <h2>7&nbsp;–&nbsp;Data Retention</h2>
      <p>We retain account and training data <strong>indefinitely</strong> to preserve your puzzle history and performance graphs. Mountainsoft LLC honors deletion requests even when that requires removing associated records from our backups. You may request deletion at any time (see Section 8).</p>

      <h2>8&nbsp;–&nbsp;Your Rights</h2>
      <ul>
        <li><strong>Access / Portability</strong> – Request a copy of the personal data we hold.</li>
        <li><strong>Correction</strong> – Update inaccurate or incomplete information.</li>
        <li><strong>Deletion (“Right to be Forgotten”)</strong> – Ask us to erase your account and stats.</li>
        <li><strong>Withdraw Consent</strong> – Where processing relies on consent, you may withdraw it.</li>
        <li><strong>CCPA “Do Not Sell”</strong> – We do not sell data, but California residents may still exercise their rights.</li>
      </ul>
      <p>Submit requests via <a href="mailto:puzzlify@puzzlify.io">puzzlify@puzzlify.io</a>. We will respond within 30 days.</p>

      <h2>9&nbsp;–&nbsp;Children’s Privacy</h2>
      <p>The Service is <strong>not directed to children under 13</strong>. We do not knowingly collect information from anyone under 13. If you are a parent or guardian and believe a child has provided us data, please contact us so we can delete it.</p>

      <h2>10&nbsp;–&nbsp;Security</h2>
      <p>We use HTTPS encryption, hashed passwords, and least‑privilege access on AWS. While no online service is 100% secure, we work to protect your data.</p>

      <h2>11&nbsp;–&nbsp;Changes to This Policy</h2>
      <p>We may update this Policy periodically. If Mountainsoft LLC makes material changes, we will post a prominent notice on the site and, where required, e‑mail registered users or obtain new consent.</p>

      <h2>12&nbsp;–&nbsp;Questions?</h2>
      <p>E‑mail us at <a href="mailto:puzzlify@puzzlify.io">puzzlify@puzzlify.io</a> and we’ll be happy to help.</p>
    </div>
  );
};

export default PrivacyPolicy;
